import { MutationFunction } from '@tanstack/react-query';
import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import type { IncomeSource } from 'src/pages/loan/income/data';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import { toIncomeSource, type LoanIncomeDto } from './mapLoanIncome';

export interface UpdateLoanIncomeAnalysisParams {
  loanId: string;
  incomeId: string;
  analysisTables: Record<string, Record<string, Record<string, unknown>>>;
  /** The analysis screen's own FIELDS, e.g. W-2's Current Pay panel. */
  incomeType?: string;
  income?: Record<string, unknown>;
}

/**
 * Saves the analysis screen's state.
 *
 * Two stores, because the screen has two kinds of input. Table cells go to
 * `analysis_json`; the panels' form fields are ordinary income fields and go
 * to `income_json`, the same place the create form writes. Sending only the
 * tables is why edits to a stream-form section used to vanish on reload.
 *
 * `incomeType` rides along because the backend validates `income` against the
 * type's field list and rejects one without the other.
 */
const updateLoanIncomeAnalysis: MutationFunction<
  IncomeSource,
  UpdateLoanIncomeAnalysisParams
> = async ({ loanId, incomeId, analysisTables, incomeType, income }) => {
  if (isMockServices()) {
    return await mock({
      id: incomeId,
      analysisTables,
    } as unknown as IncomeSource);
  }

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<{
    success: boolean;
    data: LoanIncomeDto;
  }>(`/loans/${loanId}/incomes/${incomeId}`, {
    method: 'PATCH',
    body: {
      analysis: analysisTables,
      ...(incomeType && income ? { incomeType, income } : {}),
    },
  });

  if (response.data) return toIncomeSource(response.data.data);
  throw new Error('Failed to save income analysis');
};

export default updateLoanIncomeAnalysis;
